import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import type { SalesContract } from "@/features/dashboard/types/sales-contract";
import { formatDate } from "@/utils/format-date";
import { formatNumber } from "@/utils/format-number";
import {
	IconAlertTriangle,
	IconBuilding,
	IconCalendarOff,
	IconFileExport,
	IconFileSpreadsheet,
	IconFileTypeCsv,
	IconFileTypePdf,
	IconUser,
} from "@tabler/icons-react";
import React, { useState } from "react";
import type { FC } from "react";

interface ExpiredContractsProps {
	contracts: SalesContract[];
	className?: string;
}

export const ExpiredContracts: FC<ExpiredContractsProps> = ({ contracts, className = "" }) => {
	const [showAll, setShowAll] = useState(false);

	const handleExport = (format: "csv" | "xlsx" | "pdf") => {
		alert("wala pa :)");
	};

	if (!contracts || contracts.length === 0) {
		return (
			<Card className={className}>
				<CardHeader className="pb-3">
					<div className="flex items-center justify-between">
						<CardTitle className="flex items-center gap-2 text-red-800">
							<IconCalendarOff className="h-5 w-5" />
							Expired Contracts
						</CardTitle>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline" size="sm">
									<IconFileExport className="w-3 h-3 mr-1" />
									Export
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => handleExport("csv")}>
									<IconFileTypeCsv className="w-4 h-4 mr-2" />
									Export as CSV
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleExport("xlsx")}>
									<IconFileSpreadsheet className="w-4 h-4 mr-2" />
									Export as Excel
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleExport("pdf")}>
									<IconFileTypePdf className="w-4 h-4 mr-2" />
									Export as PDF
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</CardHeader>
				<CardContent>
					<div className="text-center py-6">
						<div className="text-gray-500 text-sm">No expired contracts</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	const getDaysExpired = (endDate: string) => {
		const today = new Date();
		const expiry = new Date(endDate);
		const diffTime = today.getTime() - expiry.getTime();
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	const getDaysExpiredBadge = (endDate: string) => {
		const days = getDaysExpired(endDate);

		if (days <= 7) {
			return (
				<Badge color="red">
					Expired {days} day{days !== 1 ? "s" : ""} ago
				</Badge>
			);
		}
		if (days <= 30) {
			return <Badge color="red">Expired {days} days ago</Badge>;
		}
		if (days <= 90) {
			return <Badge color="red">Expired {Math.floor(days / 7)} weeks ago</Badge>;
		}
		return <Badge color="red">Expired {Math.floor(days / 30)} months ago</Badge>;
	};

	// Sort contracts by how recently they expired (most recent first)
	const sortedContracts = [...contracts].sort((a, b) => {
		const daysA = getDaysExpired(a.end_date);
		const daysB = getDaysExpired(b.end_date);
		return daysA - daysB;
	});

	// Determine how many contracts to show
	const contractsToShow = showAll ? sortedContracts : sortedContracts.slice(0, 5);

	return (
		<Card className={className}>
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center gap-2 text-red-800">
						<IconAlertTriangle className="h-5 w-5" />
						Expired Contracts ({contracts.length})
					</CardTitle>
					{contracts.length > 0 && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline" size="sm">
									<IconFileExport className="w-3 h-3 mr-1" />
									Export
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => handleExport("csv")}>
									<IconFileTypeCsv className="w-4 h-4 mr-2" />
									Export as CSV
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleExport("xlsx")}>
									<IconFileSpreadsheet className="w-4 h-4 mr-2" />
									Export as Excel
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleExport("pdf")}>
									<IconFileTypePdf className="w-4 h-4 mr-2" />
									Export as PDF
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			</CardHeader>
			<CardContent className="max-h-[500px] overflow-y-auto">
				<div className="space-y-3 pr-2">
					{contractsToShow.map((contract) => (
						<div key={contract.id} className="border border-red-200 rounded-lg p-4 bg-red-50/30 hover:bg-red-50/60 transition-colors">
							<div className="flex items-start justify-between">
								<div className="flex-1">
									<div className="flex items-center gap-3 mb-2">
										<h4 className="font-medium text-gray-900">{contract.project_name}</h4>
										<Badge color="red">Expired</Badge>
										{getDaysExpiredBadge(contract.end_date)}
									</div>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
										<div className="flex items-center gap-2">
											<IconBuilding className="h-4 w-4" />
											<span>{contract.client_name}</span>
										</div>

										{contract.salesperson && (
											<div className="flex items-center gap-2">
												<IconUser className="h-4 w-4" />
												<span>{contract.salesperson.name}</span>
											</div>
										)}

										<div>
											<span className="font-medium">Contract Amount:</span> ₱{formatNumber(contract.contract_amount)}
										</div>

										<div>
											<span className="font-medium">Monthly Recurring:</span> ₱{formatNumber(contract.monthly_recurring_sales)}
										</div>
									</div>

									<div className="mt-3 pt-3 border-t border-red-200">
										<div className="flex items-center justify-between text-sm">
											<div>
												<span className="text-gray-600">Started:</span> <span className="font-medium">{formatDate(contract.start_date, "PP")}</span>
											</div>
											<div>
												<span className="text-gray-600">Expired:</span> <span className="font-medium text-red-700">{formatDate(contract.end_date, "PP")}</span>
											</div>
										</div>
									</div>

									{contract.notes && (
										<div className="mt-2 pt-2 border-t border-red-200">
											<div className="text-sm text-gray-600">
												<span className="font-medium">Notes:</span> {contract.notes}
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
					))}
				</div>

				{contracts.length > 5 && (
					<div className="mt-4 text-center sticky bottom-0 bg-white pt-2">
						<Button variant="outline" size="sm" onClick={() => setShowAll(!showAll)}>
							{showAll ? "Show Less" : `View All ${contracts.length} Expired Contracts`}
						</Button>
					</div>
				)}
			</CardContent>
		</Card>
	);
};
